import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import Login from "./pages/Login";
import SetStationToken from "./pages/SetStationToken";
import Station from "./pages/Station";
import PortalHub from "./pages/PortalHub";
import DevTests from "./pages/DevTests";
import AuthGate from "./components/AuthGate";
import AdminLayout from "./components/admin/AdminLayout";
import AdminRecipes from "./pages/admin/AdminRecipes";
import AdminRecipeDetail from "./pages/admin/AdminRecipeDetail";
import AdminRecipeEditor from "./pages/admin/AdminRecipeEditor";
import AdminStaffAccess from "./pages/admin/AdminStaffAccess";
import AdminStationTablets from "./pages/admin/AdminStationTablets";
import AdminIngredients from "./pages/admin/AdminIngredients";
import AdminActivity from "./pages/admin/AdminActivity";
import AdminSettings from "./pages/admin/AdminSettings";
import AdminIssues from "./pages/admin/AdminIssues";
import AdminPrepRecipes from "./pages/admin/AdminPrepRecipes";
import AdminAliases from "./pages/admin/AdminAliases";
import AdminImportExport from "./pages/admin/AdminImportExport";
import AdminInventory from "./pages/admin/AdminInventory";
import AdminCRM from "./pages/admin/AdminCRM";
import AdminCommunications from "./pages/admin/AdminCommunications";
import AdminCuration from "./pages/admin/AdminCuration";
import AdminDashboard from "./pages/admin/AdminDashboard";
import AdminVoice from "./pages/admin/AdminVoice";
import AdminOperations from "./pages/admin/AdminOperations";
import AdminShelfMap from "./pages/admin/AdminShelfMap";
import AdminBottleChecks from "./pages/admin/AdminBottleChecks";
import AdminTastingMat from "./pages/admin/AdminTastingMat";
import AdminCompAudit from "./pages/admin/AdminCompAudit";
import AdminMembers from "./pages/admin/AdminMembers";
import AdminWelcomeKits from "./pages/admin/AdminWelcomeKits";
import AdminClubPours from "./pages/admin/AdminClubPours";
import AdminCashManagement from "./pages/admin/AdminCashManagement";
import AdminMerge from "./pages/admin/AdminMerge";
import AdminFlights from "./pages/admin/AdminFlights";
import AdminCatalogMonitor from "./pages/admin/AdminCatalogMonitor";
import AdminCharity from "./pages/admin/AdminCharity";
import AdminCocktailMapping from "./pages/admin/AdminCocktailMapping";
import AdminEvents from "./pages/admin/AdminEvents";
import AdminReviews from "./pages/admin/AdminReviews";
import AdminPickList from "./pages/admin/AdminPickList";
import AdminInventoryCount from "./pages/admin/AdminInventoryCount";
import AdminTemplates from "./pages/admin/AdminTemplates";
import AdminPartners from "./pages/admin/AdminPartners";
import AdminArticles from "./pages/admin/AdminArticles";
import AdminBottlePhotos from "./pages/admin/AdminBottlePhotos";
import AdminWhiskeyIntel from "./pages/admin/AdminWhiskeyIntel";
import AdminTrails from "./pages/admin/AdminTrails";
import AdminSuggestedEdits from "./pages/admin/AdminSuggestedEdits";
import AdminBusinessContext from "./pages/admin/AdminBusinessContext";
import AdminKnowledge from "./pages/admin/AdminKnowledge";
import AdminEnrichment from "./pages/admin/AdminEnrichment";
import AdminSEO from "./pages/admin/AdminSEO";
import HostLayout from "./components/host/HostLayout";
import HostDashboard from "./pages/host/HostDashboard";
import HostIntake from "./pages/host/HostIntake";
import HostSettings from "./pages/host/HostSettings";
import { UpdatePrompt } from "./components/UpdatePrompt";

export default function App() {
  // PWA Ghost Session memory leak prevention (4 AM soft reload)
  useEffect(() => {
    const handle = window.setInterval(() => {
      const now = new Date();
      const hr = now.getHours();
      if (hr === 4) {
        const todayStr = now.toISOString().split("T")[0];
        const lastReload = localStorage.getItem("sentinel.last_ghost_reload");
        if (lastReload !== todayStr) {
          localStorage.setItem("sentinel.last_ghost_reload", todayStr);
          window.location.reload();
        }
      }
    }, 60_000); // Check once a minute

    return () => window.clearInterval(handle);
  }, []);

  return (
    <>
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/set-station-token" element={<SetStationToken />} />
      <Route path="/dev/tests" element={<DevTests />} />

      {/* Admin workspace — gated inside AdminLayout (staff allowed for floor tools, bar_manager+ for full admin)
          and double-gated via AuthGate role-checking. */}
      <Route
        path="/admin"
        element={
          <AuthGate allowedRoles={["bar_staff", "bar_bartender", "bar_lead", "bar_manager", "bar_owner"]}>
            <AdminLayout />
          </AuthGate>
        }
      >
        <Route index element={<Navigate to="recipes" replace />} />
        <Route path="recipes" element={<AdminRecipes />} />
        <Route path="recipes/:slug" element={<AdminRecipeDetail />} />
        <Route
          path="recipes/:slug/edit/:versionId"
          element={<AdminRecipeEditor />}
        />
        <Route path="recipes/:slug/aliases" element={<AdminAliases />} />
        <Route path="prep-recipes" element={<AdminPrepRecipes />} />
        <Route path="prep-recipes/:slug" element={<AdminPrepRecipes />} />
        <Route path="ingredients" element={<AdminIngredients />} />
        <Route path="inventory" element={<AdminInventory />} />
        <Route path="crm" element={<AdminCRM />} />
        <Route path="communications" element={<AdminCommunications />} />
        <Route path="curation" element={<AdminCuration />} />
        <Route path="dashboard" element={<AdminDashboard />} />
        <Route path="settings/voice" element={<AdminVoice />} />
        <Route path="operations" element={<AdminOperations />} />
        <Route path="shelf-map" element={<AdminShelfMap />} />
        <Route path="bottle-checks" element={<AdminBottleChecks />} />
        <Route path="tasting-mat" element={<AdminTastingMat />} />
        <Route path="issues" element={<AdminIssues />} />
        <Route path="activity" element={<AdminActivity />} />
        <Route path="comp-audit" element={<AdminCompAudit />} />
        <Route path="members" element={<AdminMembers />} />
        <Route path="welcome-kits" element={<AdminWelcomeKits />} />
        <Route path="club-pours" element={<AdminClubPours />} />
        <Route path="cash-management" element={<AdminCashManagement />} />
        <Route path="merge" element={<AdminMerge />} />
        <Route path="flights" element={<AdminFlights />} />
        <Route path="catalog-monitor" element={<AdminCatalogMonitor />} />
        <Route path="charity" element={<AdminCharity />} />
        <Route path="cocktail-mapping" element={<AdminCocktailMapping />} />
        <Route path="events" element={<AdminEvents />} />
        <Route path="reviews" element={<AdminReviews />} />
        <Route path="pick-list" element={<AdminPickList />} />
        <Route path="inventory-count" element={<AdminInventoryCount />} />
        <Route path="templates" element={<AdminTemplates />} />
        <Route path="partners" element={<AdminPartners />} />
        <Route path="articles" element={<AdminArticles />} />
        <Route path="bottle-photos" element={<AdminBottlePhotos />} />
        <Route path="whiskey-intel" element={<AdminWhiskeyIntel />} />
        <Route path="trails" element={<AdminTrails />} />
        <Route path="suggested-edits" element={<AdminSuggestedEdits />} />
        <Route path="business-context" element={<AdminBusinessContext />} />
        <Route path="knowledge" element={<AdminKnowledge />} />
        <Route path="enrichment" element={<AdminEnrichment />} />
        <Route path="seo" element={<AdminSEO />} />
        <Route path="import-export" element={<AdminImportExport />} />
        <Route path="settings" element={<AdminSettings />} />
        <Route path="staff-access" element={<AdminStaffAccess />} />
        <Route
          path="station-tablets"
          element={
            <AuthGate allowedRoles={["bar_owner"]}>
              <AdminStationTablets />
            </AuthGate>
          }
        />
      </Route>

      {/* Host stand ledger — bar_host and management roles */}
      <Route
        path="/host"
        element={
          <AuthGate allowedRoles={["bar_host", "bar_manager", "bar_owner"]}>
            <HostLayout />
          </AuthGate>
        }
      >
        <Route index element={<Navigate to="dashboard" replace />} />
        <Route path="dashboard" element={<HostDashboard />} />
        <Route path="intake" element={<HostIntake />} />
        <Route
          path="settings"
          element={
            <AuthGate allowedRoles={["bar_owner"]}>
              <HostSettings />
            </AuthGate>
          }
        />
      </Route>

      {/* Unified Staff OS Portal Hub (Home page) */}
      <Route
        path="/"
        element={
          <AuthGate>
            {(user) => <PortalHub user={user} />}
          </AuthGate>
        }
      />

      {/* Bartender Station Mode */}
      <Route
        path="/station"
        element={
          <AuthGate allowedRoles={["bar_staff", "bar_bartender", "bar_lead", "bar_manager", "bar_owner"]}>
            <Station />
          </AuthGate>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    <UpdatePrompt />
    </>
  );
}
